import React, { useState, useRef, useEffect } from 'react';
import { cropImage } from './cropperUtils';
import ImageUploader from '../../components/ImageUploader/ImageUploader';
import './Cropper.css';

const Cropper = () => {
  const [originalImage, setOriginalImage] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isCropping, setIsCropping] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [activeHandle, setActiveHandle] = useState(null);
  const [displayRatio, setDisplayRatio] = useState(1);
  
  const canvasRef = useRef(null);
  const cropCanvasRef = useRef(null);
  const canvasContainerRef = useRef(null);
  
  // Initialize canvas when image is loaded
  useEffect(() => {
    if (originalImage && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Set canvas dimensions to match image
      canvas.width = originalImage.width;
      canvas.height = originalImage.height;
      
      // Calculate the display ratio to make the image smaller
      const containerWidth = canvasContainerRef.current.clientWidth;
      const maxDisplayWidth = Math.min(containerWidth, 400); // Reduce max width to 400px for side-by-side layout
      
      // Calculate the ratio to scale down the image
      const ratio = maxDisplayWidth / canvas.width;
      setDisplayRatio(ratio);
      
      // Apply the scale to the canvas display size, not the actual canvas dimensions
      canvas.style.width = `${canvas.width * ratio}px`;
      canvas.style.height = `${canvas.height * ratio}px`;
      
      // Draw image on canvas
      ctx.drawImage(originalImage, 0, 0, canvas.width, canvas.height);
      
      // Make sure it fits the visible container
      const visibleWidth = canvas.width;
      const visibleHeight = canvas.height;
      
      // Initialize crop area to default (center 80% of image)
      const defaultWidth = Math.round(visibleWidth * 0.8);
      const defaultHeight = Math.round(visibleHeight * 0.8);
      const defaultX = Math.round((canvas.width - defaultWidth) / 2);
      const defaultY = Math.round((canvas.height - defaultHeight) / 2);
      
      setCropArea({
        x: defaultX,
        y: defaultY,
        width: defaultWidth,
        height: defaultHeight
      });
      
      // Clear any previous cropped image when loading a new one
      setCroppedImage(null);
    }
  }, [originalImage]);

  // Draw crop overlay when crop area changes
  useEffect(() => {
    if (originalImage && canvasRef.current && cropArea.width > 0) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Clear canvas and redraw original image
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(originalImage, 0, 0, canvas.width, canvas.height);
      
      // Create a semi-transparent overlay
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Instead of clearing the crop area, redraw that part of the original image
      ctx.save();
      ctx.beginPath();
      ctx.rect(cropArea.x, cropArea.y, cropArea.width, cropArea.height);
      ctx.clip();
      ctx.clearRect(cropArea.x, cropArea.y, cropArea.width, cropArea.height);
      ctx.drawImage(originalImage, 0, 0, canvas.width, canvas.height);
      ctx.restore();
      
      // Draw crop area border
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 2;
      ctx.strokeRect(cropArea.x, cropArea.y, cropArea.width, cropArea.height);
      
      // Draw resize handles
      const handleSize = 10;
      ctx.fillStyle = 'white';
      
      // Corner handles
      ctx.fillRect(cropArea.x - handleSize / 2, cropArea.y - handleSize / 2, handleSize, handleSize); // Top-left
      ctx.fillRect(cropArea.x + cropArea.width - handleSize / 2, cropArea.y - handleSize / 2, handleSize, handleSize); // Top-right
      ctx.fillRect(cropArea.x - handleSize / 2, cropArea.y + cropArea.height - handleSize / 2, handleSize, handleSize); // Bottom-left
      ctx.fillRect(cropArea.x + cropArea.width - handleSize / 2, cropArea.y + cropArea.height - handleSize / 2, handleSize, handleSize); // Bottom-right
      
      // Middle side handles
      ctx.fillRect(cropArea.x + cropArea.width / 2 - handleSize / 2, cropArea.y - handleSize / 2, handleSize, handleSize); // Top-middle
      ctx.fillRect(cropArea.x + cropArea.width - handleSize / 2, cropArea.y + cropArea.height / 2 - handleSize / 2, handleSize, handleSize); // Right-middle
      ctx.fillRect(cropArea.x + cropArea.width / 2 - handleSize / 2, cropArea.y + cropArea.height - handleSize / 2, handleSize, handleSize); // Bottom-middle
      ctx.fillRect(cropArea.x - handleSize / 2, cropArea.y + cropArea.height / 2 - handleSize / 2, handleSize, handleSize); // Left-middle
    }
  }, [originalImage, cropArea]);

  const handleImageSelected = (img) => {
    setOriginalImage(img);
    setCroppedImage(null);
  };

  const getCursorPosition = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };
  
  const isOverHandle = (x, y) => {
    if (!cropArea) return null;
    
    const handleSize = 10;
    const handles = {
      'tl': { x: cropArea.x, y: cropArea.y },
      'tm': { x: cropArea.x + cropArea.width / 2, y: cropArea.y },
      'tr': { x: cropArea.x + cropArea.width, y: cropArea.y },
      'rm': { x: cropArea.x + cropArea.width, y: cropArea.y + cropArea.height / 2 },
      'br': { x: cropArea.x + cropArea.width, y: cropArea.y + cropArea.height },
      'bm': { x: cropArea.x + cropArea.width / 2, y: cropArea.y + cropArea.height },
      'bl': { x: cropArea.x, y: cropArea.y + cropArea.height },
      'lm': { x: cropArea.x, y: cropArea.y + cropArea.height / 2 }
    };
    
    for (const [handle, pos] of Object.entries(handles)) {
      if (Math.abs(x - pos.x) <= handleSize / 2 && Math.abs(y - pos.y) <= handleSize / 2) {
        return handle;
      }
    }
    
    // Check if inside crop area
    if (x >= cropArea.x && x <= cropArea.x + cropArea.width &&
        y >= cropArea.y && y <= cropArea.y + cropArea.height) {
      return 'inside';
    }
    
    return null;
  };

  // Add window event listeners to handle mouse events outside the canvas
  useEffect(() => {
    if (originalImage) {
      const handleMouseUp = () => {
        setIsDragging(false);
        setActiveHandle(null);
      };
      
      const handleMouseMove = (e) => {
        if ((isDragging || activeHandle) && canvasRef.current) {
          const canvas = canvasRef.current;
          const rect = canvas.getBoundingClientRect();
          const scaleX = canvas.width / rect.width;
          const scaleY = canvas.height / rect.height;
          
          const x = (e.clientX - rect.left) * scaleX;
          const y = (e.clientY - rect.top) * scaleY;
          
          if (isDragging) {
            const deltaX = x - startPoint.x;
            const deltaY = y - startPoint.y;
            
            // Update crop area position within canvas bounds
            const newX = Math.max(0, Math.min(canvas.width - cropArea.width, cropArea.x + deltaX));
            const newY = Math.max(0, Math.min(canvas.height - cropArea.height, cropArea.y + deltaY));
            
            // Calculate visible area of the canvas based on container
            const containerWidth = canvasContainerRef.current.clientWidth;
            const containerHeight = canvasContainerRef.current.clientHeight;
            const visibleWidth = containerWidth * scaleX;
            const visibleHeight = containerHeight * scaleY;
            
            // Ensure crop area stays fully visible within container
            const visibleAreaX = Math.max(0, canvas.width - visibleWidth);
            const visibleAreaY = Math.max(0, canvas.height - visibleHeight);
            
            // Constrain X to keep crop area visible in container
            const constrainedX = newX < visibleAreaX ? visibleAreaX : 
                                 newX + cropArea.width > visibleAreaX + visibleWidth ? 
                                 visibleAreaX + visibleWidth - cropArea.width : newX;
            
            // Constrain Y to keep crop area visible in container
            const constrainedY = newY < visibleAreaY ? visibleAreaY : 
                                 newY + cropArea.height > visibleAreaY + visibleHeight ? 
                                 visibleAreaY + visibleHeight - cropArea.height : newY;
            
            setCropArea(prev => ({
              ...prev,
              x: constrainedX,
              y: constrainedY
            }));
            
            setStartPoint({ x, y });
          } else if (activeHandle) {
            const deltaX = x - startPoint.x;
            const deltaY = y - startPoint.y;
            let newCropArea = { ...cropArea };
            
            // Minimum size for crop area
            const minSize = 20;
            
            // Calculate visible area of the canvas based on container
            const containerWidth = canvasContainerRef.current.clientWidth;
            const containerHeight = canvasContainerRef.current.clientHeight;
            const visibleWidth = containerWidth * scaleX;
            const visibleHeight = containerHeight * scaleY;
            const visibleAreaX = Math.max(0, canvas.width - visibleWidth);
            const visibleAreaY = Math.max(0, canvas.height - visibleHeight);
            
            // Maximum crop dimensions to stay within visible area
            const maxWidth = visibleWidth;
            const maxHeight = visibleHeight;
            
            // Handle resize logic based on active handle
            switch (activeHandle) {
              case 'tl': // Top-left
                newCropArea = {
                  x: Math.min(cropArea.x + deltaX, cropArea.x + cropArea.width - minSize),
                  y: Math.min(cropArea.y + deltaY, cropArea.y + cropArea.height - minSize),
                  width: Math.max(cropArea.width - deltaX, minSize),
                  height: Math.max(cropArea.height - deltaY, minSize)
                };
                break;
              case 'tm': // Top-middle
                newCropArea = {
                  ...cropArea,
                  y: Math.min(cropArea.y + deltaY, cropArea.y + cropArea.height - minSize),
                  height: Math.max(cropArea.height - deltaY, minSize)
                };
                break;
              case 'tr': // Top-right
                newCropArea = {
                  ...cropArea,
                  y: Math.min(cropArea.y + deltaY, cropArea.y + cropArea.height - minSize),
                  width: Math.min(Math.max(cropArea.width + deltaX, minSize), maxWidth, canvas.width - cropArea.x),
                  height: Math.max(cropArea.height - deltaY, minSize)
                };
                break;
              case 'rm': // Right-middle
                newCropArea = {
                  ...cropArea,
                  width: Math.min(Math.max(cropArea.width + deltaX, minSize), maxWidth, canvas.width - cropArea.x)
                };
                break;
              case 'br': // Bottom-right
                newCropArea = {
                  ...cropArea,
                  width: Math.min(Math.max(cropArea.width + deltaX, minSize), maxWidth, canvas.width - cropArea.x),
                  height: Math.min(Math.max(cropArea.height + deltaY, minSize), maxHeight, canvas.height - cropArea.y)
                };
                break;
              case 'bm': // Bottom-middle
                newCropArea = {
                  ...cropArea,
                  height: Math.min(Math.max(cropArea.height + deltaY, minSize), maxHeight, canvas.height - cropArea.y)
                };
                break;
              case 'bl': // Bottom-left
                newCropArea = {
                  x: Math.min(cropArea.x + deltaX, cropArea.x + cropArea.width - minSize),
                  y: cropArea.y,
                  width: Math.max(cropArea.width - deltaX, minSize),
                  height: Math.min(Math.max(cropArea.height + deltaY, minSize), maxHeight, canvas.height - cropArea.y)
                };
                break;
              case 'lm': // Left-middle
                newCropArea = {
                  x: Math.min(cropArea.x + deltaX, cropArea.x + cropArea.width - minSize),
                  y: cropArea.y,
                  width: Math.max(cropArea.width - deltaX, minSize),
                  height: cropArea.height
                };
                break;
              default:
                break;
            }
            
            // Ensure crop area stays within canvas bounds and visible container
            newCropArea.x = Math.max(visibleAreaX, Math.min(canvas.width - newCropArea.width, newCropArea.x));
            newCropArea.y = Math.max(visibleAreaY, Math.min(canvas.height - newCropArea.height, newCropArea.y));
            
            // Ensure crop area doesn't exceed max dimensions
            newCropArea.width = Math.min(newCropArea.width, maxWidth, canvas.width - newCropArea.x);
            newCropArea.height = Math.min(newCropArea.height, maxHeight, canvas.height - newCropArea.y);
            
            setCropArea(newCropArea);
            setStartPoint({ x, y });
          }
        }
      };
      
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('mousemove', handleMouseMove);
      
      return () => {
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [originalImage, isDragging, activeHandle, startPoint, cropArea]);

  const handleCanvasMouseDown = (e) => {
    if (!originalImage) return;
    
    const { x, y } = getCursorPosition(e);
    const handle = isOverHandle(x, y);
    
    setStartPoint({ x, y });
    
    if (handle === 'inside') {
      setIsDragging(true);
    } else if (handle) {
      setActiveHandle(handle);
    }
  };

  // We'll remove the mousemove and mouseup handlers from the canvas, since we're using window events now
  const handleCanvasMouseMove = (e) => {
    if (!originalImage) return;
    
    const canvas = canvasRef.current;
    const { x, y } = getCursorPosition(e);
    
    // Update cursor based on where it is
    const handle = isOverHandle(x, y);
    if (handle) {
      let cursor = 'move';
      if (['tl', 'br'].includes(handle)) cursor = 'nwse-resize';
      else if (['tr', 'bl'].includes(handle)) cursor = 'nesw-resize';
      else if (['tm', 'bm'].includes(handle)) cursor = 'ns-resize';
      else if (['lm', 'rm'].includes(handle)) cursor = 'ew-resize';
      canvas.style.cursor = cursor;
    } else {
      canvas.style.cursor = 'crosshair';
    }
  };

  // We won't need the canvas mouseup handler anymore
  const handleCanvasMouseUp = () => {};

  const handleCrop = async () => {
    if (!originalImage || cropArea.width <= 0 || cropArea.height <= 0) return;
    
    setIsCropping(true);
    try {
      const result = await cropImage(originalImage, cropArea, cropCanvasRef.current);
      setCroppedImage(result);
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setIsCropping(false);
    }
  };

  const handleDownload = () => {
    if (!croppedImage) return;
    
    const link = document.createElement('a');
    link.href = croppedImage;
    link.download = 'cropped-image.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="cropper tool-container">
      <div className="container">
        <div className="tool-header">
          <h1>Image Cropper</h1>
          <p>Crop your images with precision</p>
        </div>
        
        <div className="tool-content">
          <ImageUploader 
            onImageSelected={handleImageSelected}
            selectedImage={originalImage}
          />
          
          {originalImage && (
            <div className="cropper-workspace">
              <div className="cropper-layout">
                <div className="cropper-left-panel">
                  <h3>Original Image</h3>
                  <div 
                    className="canvas-container" 
                    ref={canvasContainerRef}
                  >
                    <canvas 
                      ref={canvasRef}
                      onMouseDown={handleCanvasMouseDown}
                      onMouseMove={handleCanvasMouseMove}
                      className="crop-canvas"
                    />
                  </div>
                  
                  <div className="crop-controls">
                    <div className="crop-dimensions">
                      <span>Selection: {Math.round(cropArea.width)} x {Math.round(cropArea.height)}px</span>
                    </div>
                    <button 
                      className="btn btn-primary crop-btn" 
                      onClick={handleCrop}
                      disabled={isCropping}
                    >
                      {isCropping ? 'Processing...' : 'Crop Image'}
                    </button>
                  </div>
                </div>
                
                <div className="cropper-right-panel">
                  <h3>Cropped Result</h3>
                  <div className="cropped-result">
                    <div className="image-preview">
                      {croppedImage ? (
                        <img src={croppedImage} alt="Cropped" />
                      ) : (
                        <div className="placeholder-image">
                          <p>Crop your image to see the result here</p>
                        </div>
                      )}
                    </div>
                    
                    {croppedImage && (
                      <div className="result-actions">
                        <button className="btn btn-primary download-btn" onClick={handleDownload}>
                          Download Cropped Image
                        </button>
                        <button 
                          className="btn btn-secondary crop-again-btn" 
                          onClick={() => setCroppedImage(null)}
                        >
                          Crop Again
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Hidden canvas for processing the cropped image */}
          <canvas ref={cropCanvasRef} style={{ display: 'none' }}></canvas>
        </div>
      </div>
    </div>
  );
};

export default Cropper; 